import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { SHOWROOM_PATH_IDENTIFIER } from "@faire/retailer-shared/categories/pathIdentifier";
import { BaseFiniteScrollCarousel } from "@faire/retailer-visitor-shared/components/Carousel/FiniteScrollCarousel/BaseFiniteScrollCarousel";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import { ShowroomTileBadge } from "@faire/retailer-visitor-shared/components/Showroom/ShowroomTileBadge";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer-visitor-shared/lib/verticalAspectRatio";
import { optimizeProductImageUrlFillVertical } from "@faire/web--source/common/images/optimizeImages";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { formatMoney, RoundingStrategy } from "@faire/web--source/common/money";
import { parseQueryParams } from "@faire/web--source/common/query-params/parseQueryParams";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import trackClick from "@faire/web-api--source/api/click-track/post";
import { trackApparelDiscoveryShowroomV2Click } from "@faire/web-api--source/events/apparelDiscovery/click/showroomV2";
import { trackApparelDiscoveryShowroomV2View } from "@faire/web-api--source/events/apparelDiscovery/view/showroomV2";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { path as categoryShowroom } from "@faire/web-api--source/routes/category/Showroom";
import productRoute from "@faire/web-api--source/routes/product/productToken";
import { observer } from "mobx-react";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { FiniteScrollCarousel } from "@faire/retailer/components/Carousel/FiniteScrollCarousel/FiniteScrollCarousel";
import { MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE } from "@faire/retailer/features/Marketplace/styleConsts";
var ITEMS_PER_SLIDES = 2;
var ITEMS_GAP = 4;
var IMAGE_WIDTH = 300;
var SingleProductTile = function(param) {
    var leanProductTile = param.leanProductTile, onClick = param.onClick, category = param.category, filterKeys = param.filterKeys;
    var product = leanProductTile === null || leanProductTile === void 0 ? void 0 : leanProductTile.product;
    var isSSR = useIsSSR();
    if (!leanProductTile || !(product === null || product === void 0 ? void 0 : product.token)) {
        return null;
    }
    var image = leanProductTile.best_image;
    var _leanProductTile_min_option_wholesale_price;
    return /*#__PURE__*/ _jsxs(StyledLink, {
        to: productRoute(product.token, {
            refC: category === null || category === void 0 ? void 0 : category.navigationPath,
            refF: (filterKeys === null || filterKeys === void 0 ? void 0 : filterKeys.length) ? filterKeys.join(",") : undefined
        }),
        onClick: onClick(product.token, product.token, IClickInfo.Type.PRODUCT),
        children: [
            /*#__PURE__*/ _jsx(StyledShowroomBadge, {
                customEventName: "showroom-product-carousel-banner",
                token: product.token
            }),
            /*#__PURE__*/ _jsx(AspectRatioPicture, {
                $width: "100%",
                $ratio: VERTICAL_ASPECT_RATIO,
                $hideBorder: true,
                children: /*#__PURE__*/ _jsx("img", {
                    src: image ? optimizeProductImageUrlFillVertical(image, isSSR, IMAGE_WIDTH) : EAEAEA_PIXEL
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                color: Core.text.inverse,
                variant: "paragraphSansRegular",
                maxLines: 2,
                children: product.name
            }),
            /*#__PURE__*/ _jsx(WholesalePrice, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "2vvNzb",
                    defaultMessage: [
                        {
                            type: 1,
                            value: "wholesalePrice"
                        },
                        {
                            type: 0,
                            value: " WSP"
                        }
                    ],
                    values: DEPRECATED_backwardsCompatibleValues({
                        wholesalePrice: formatMoney({
                            money: (_leanProductTile_min_option_wholesale_price = leanProductTile.min_option_wholesale_price) !== null && _leanProductTile_min_option_wholesale_price !== void 0 ? _leanProductTile_min_option_wholesale_price : IFaireMoney.build({
                                amount_cents: 0
                            }),
                            roundingStrategy: RoundingStrategy.DROP_CENTS
                        })
                    })
                })
            })
        ]
    });
};
var ShowroomEducationCarouselBase = function(param) {
    var layoutElement = param.layoutElement, className = param.className, requestId = param.requestId, category = param.category;
    var peekGapPercentage = useResponsiveValue({
        mobileAndAbove: 8,
        tabletAndAbove: 18,
        xLargeAndAbove: 27
    });
    var eventName = "".concat(layoutElement.type, "-").concat(category === null || category === void 0 ? void 0 : category.mostSpecificPathIdentifier);
    var onClick = function(eventSource, token, clickType) {
        return function() {
            trackApparelDiscoveryShowroomV2Click(eventName, eventSource, requestId !== null && requestId !== void 0 ? requestId : "");
            if (token && clickType) {
                trackClick(IClickInfo.build({
                    token: token,
                    type: clickType,
                    container_name: eventName,
                    request_id: requestId
                }));
            }
        };
    };
    var products = layoutElement.layout_element_data.map(function(layoutElementData) {
        return layoutElementData.lean_product_tile;
    }).filter(isNotUndefined);
    var searchString = useLocation().search;
    var filters = parseQueryParams(searchString).get("filters");
    var filterKeys = filters ? filters.split(",").filter(isNotUndefined) : [];
    var renderTile = function(leanProductTile, index) {
        return /*#__PURE__*/ _jsx(SingleProductTile, {
            leanProductTile: leanProductTile,
            onClick: onClick,
            category: category,
            filterKeys: filterKeys
        }, "shop-showroom-product-carousel-".concat(index));
    };
    var onVisible = function() {
        trackApparelDiscoveryShowroomV2View(eventName, requestId !== null && requestId !== void 0 ? requestId : "", "");
    };
    var ref = useDoActionWhenVisible(onVisible);
    return /*#__PURE__*/ _jsx("div", {
        className: className,
        ref: ref,
        children: /*#__PURE__*/ _jsxs(Wrapper, {
            children: [
                /*#__PURE__*/ _jsxs(TextWrapper, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "displaySSerifRegular",
                            color: Core.text.inverse,
                            children: layoutElement.main_text
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "1x"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraphSansRegular",
                            color: Core.text.inverse,
                            children: layoutElement.description_text
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "4x"
                        }),
                        /*#__PURE__*/ _jsx(StyledShopAllButton, {
                            linkTo: layoutElement.cta_link,
                            onClick: onClick("shop-all-".concat(layoutElement.category_path_identifier), layoutElement.category_path_identifier, IClickInfo.Type.CATEGORY),
                            children: layoutElement.cta_text
                        }),
                        /*#__PURE__*/ _jsx(StyledAboutShowroomButton, {
                            linkTo: categoryShowroom(),
                            variant: "tertiary",
                            onClick: onClick("about-showroom", SHOWROOM_PATH_IDENTIFIER, IClickInfo.Type.CATEGORY),
                            children: layoutElement.button_cta_text
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(StyledFiniteScrollCarousel, {
                    itemsPerSlide: ITEMS_PER_SLIDES,
                    items: products,
                    renderItem: renderTile,
                    itemGap: ITEMS_GAP,
                    peekGapPercentage: peekGapPercentage,
                    hideWhenEndReached: true,
                    name: "showroom-education-carousel"
                }),
                /*#__PURE__*/ _jsx(BlackOverlay, {})
            ]
        })
    });
};
export var ShowroomEducationCarousel = observer(ShowroomEducationCarouselBase);
var Wrapper = styled(Flex).attrs({
    align: "center",
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row"
    }
}).withConfig({
    componentId: "sc-67b23857-0"
})([
    "background-color:",
    ";width:100%;padding:",
    ";position:relative;height:fit-content;margin-bottom:",
    ";",
    "{padding:",
    ";}",
    "{padding:",
    ";}"
], Core.surface.enabled, getSpacing("3x", 0, "4x"), getSpacing("3x"), responsiveMediaQueries.tabletAndAbove, getSpacing("4x", 0, "3x"), responsiveMediaQueries.desktopAndAbove, getSpacing("5x", 0, "4x"));
var TextWrapper = styled(Column).withConfig({
    componentId: "sc-67b23857-1"
})([
    "width:200px;flex-shrink:0;",
    "{width:100%;padding:",
    ";}",
    "{margin:",
    ";}",
    "{margin:",
    ";}",
    "{margin:",
    ";}"
], responsiveMediaQueries.mobile, getSpacing(0, MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE, "3x", MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE), responsiveMediaQueries.tablet, getSpacing(0, "4x"), responsiveMediaQueries.desktop, getSpacing(0, "5x"), responsiveMediaQueries.xLargeAndAbove, getSpacing(0, "7x"));
var StyledShopAllButton = styled(Button).attrs({
    variant: "secondary"
}).withConfig({
    componentId: "sc-67b23857-2"
})([
    "",
    "{width:168px;}&:active,&:focus,&:hover{background-color:",
    ";color:",
    ";}"
], responsiveMediaQueries.mobile, Core.surface.inverse, Core.text.default);
var WholesalePrice = styled(Typography).attrs({
    color: Core.text.subdued,
    variant: "paragraphSansRegular"
}).withConfig({
    componentId: "sc-67b23857-3"
})([
    "transition:color 300ms ease-in-out;"
]);
var StyledLink = styled(Link).withConfig({
    componentId: "sc-67b23857-4"
})([
    "display:flex;flex-direction:column;align-items:flex-start;position:relative;",
    "{&:hover{",
    "{color:",
    ";}}}"
], responsiveMediaQueries.canHover, WholesalePrice, Core.text.inverse);
var StyledFiniteScrollCarousel = styled(FiniteScrollCarousel.FiniteScrollCarousel).withConfig({
    componentId: "sc-67b23857-5"
})([
    "min-width:0;",
    ",",
    "{color:white;z-index:2;width:40px;height:40px;top:calc(50% - 27px);}",
    "{left:-38px;}",
    "{right:8px;}",
    "{",
    "{padding-left:",
    "px;}}"
], BaseFiniteScrollCarousel.Previous, BaseFiniteScrollCarousel.Next, BaseFiniteScrollCarousel.Previous, BaseFiniteScrollCarousel.Next, responsiveMediaQueries.mobile, BaseFiniteScrollCarousel.Scrollable, MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE);
var BlackOverlay = styled.div.withConfig({
    componentId: "sc-67b23857-6"
})([
    "width:238px;height:100%;background:linear-gradient( 270deg,",
    " 0%,rgba(0,0,0,0) 88.01% );position:absolute;right:0;top:0;z-index:1;pointer-events:none;",
    "{display:none;}"
], Core.surface.enabled, responsiveMediaQueries.mobile);
var StyledShowroomBadge = styled(ShowroomTileBadge).withConfig({
    componentId: "sc-67b23857-7"
})([
    "position:absolute;top:8px;left:8px;z-index:1;"
]);
var StyledAboutShowroomButton = styled(Button).withConfig({
    componentId: "sc-67b23857-8"
})([
    "&&&{text-decoration-color:",
    ";width:fit-content;padding-top:",
    ";color:",
    ";}"
], Core.text.inverse, getSpacing("2x"), Core.text.inverse);
